import { supabase } from '@/integrations/supabase/client';

export interface MergeAnalysisRun {
  id: string;
  parent_bot_id: string;
  child_bot_ids: string[];
  config: any;
  equity_curve: any; // JSON in database
  stats: any;
  published: boolean;
  is_current: boolean;
  title?: string;
  notes?: string;
  instrument?: string;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface EquityPoint {
  t?: string; // timestamp
  trade_index: number;
  pnl: number;
  equity: number;
  instrument?: string; // Per-point instrument information
}

export interface PublishMergeAnalysisPayload {
  parent_bot_id: string;
  child_bot_ids: string[];
  config: any;
  equity_curve: any; // Will be converted to JSON
  stats: any;
  published?: boolean;
  is_current?: boolean;
  title?: string;
  notes?: string;
  instrument?: string;
}

export class MergeAnalysisService {
  static async publishMergeAnalysis(payload: PublishMergeAnalysisPayload): Promise<MergeAnalysisRun> {
    const { data, error } = await supabase
      .from('bot_merge_analysis_runs')
      .insert({
        parent_bot_id: payload.parent_bot_id,
        child_bot_ids: payload.child_bot_ids,
        config: payload.config,
        equity_curve: payload.equity_curve,
        stats: payload.stats,
        published: payload.published ?? true,
        is_current: payload.is_current ?? true,
        title: payload.title,
        notes: payload.notes,
        instrument: payload.instrument,
        created_by: (await supabase.auth.getUser()).data.user?.id || ''
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  static async getLatestPublishedMergeAnalysis(parentBotId: string): Promise<MergeAnalysisRun | null> {
    const { data, error } = await supabase
      .from('bot_merge_analysis_runs')
      .select('*')
      .eq('parent_bot_id', parentBotId)
      .eq('published', true)
      .order('is_current', { ascending: false })
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (error) throw error;
    return data;
  }

  static async getCountChildrenFromConfig(config: any): Promise<number> {
    if (!config || typeof config !== 'object') return 0;
    
    // Extract unique child bot IDs from config
    const childBotIds = new Set<string>();
    
    // Assuming config structure has bot-specific settings with bot IDs as keys
    Object.keys(config).forEach(key => {
      if (key.includes('bot_') || key.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i)) {
        childBotIds.add(key);
      }
    });
    
    return childBotIds.size;
  }

  static async getMergeAnalysisRuns(parentBotId: string): Promise<MergeAnalysisRun[]> {
    const { data, error } = await supabase
      .from('bot_merge_analysis_runs')
      .select('*')
      .eq('parent_bot_id', parentBotId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  }

  static async deleteMergeAnalysisRun(runId: string): Promise<void> {
    const { error } = await supabase
      .from('bot_merge_analysis_runs')
      .delete()
      .eq('id', runId);

    if (error) throw error;
  }

  static async updateMergeAnalysisRun(runId: string, updates: Partial<MergeAnalysisRun>): Promise<MergeAnalysisRun> {
    const { data, error } = await supabase
      .from('bot_merge_analysis_runs')
      .update(updates)
      .eq('id', runId)
      .select()
      .single();

    if (error) throw error;
    return data;
  }
}